import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  AILabel,
  InlineLoading,
  InlineNotification,
  OperationalTag,
  Tile,
  ToggletipContent,
} from "@carbon/react";
import { useOfferAssistant } from "../../hooks/useOfferAssistant";
import { OfferAssistantRecommendation } from "../../types";
import OfferAssistantComposer from "./OfferAssistantComposer";
import OfferAssistantMessages from "./OfferAssistantMessages";
import OfferAssistantRecommendationCard from "./OfferAssistantRecommendationCard";
import {
  latestRecommendationMessage,
  SUGGESTED_PROMPTS,
} from "./offerAssistantUtils";
import "./offerAssistant.scss";

export type OfferAssistantPanelProps = {
  /** Single award. */
  orderId?: string;
  /** Joint award. */
  multipleOrderId?: string;
  /** The recommendation currently marked in the matrix (drives the summary card). */
  activeRecommendation: OfferAssistantRecommendation | null;
  /** A NEW answer brought a recommendation: mark it and pre-select it. Fires once per answer. */
  onRecommendation: (recommendation: OfferAssistantRecommendation) => void;
  /** Show an earlier recommendation (conversation history): mark only, no pre-selection. */
  onShowRecommendation: (recommendation: OfferAssistantRecommendation) => void;
  /** "Aplicar selección" / "Aplicar proveedor único": pre-select these offers. */
  onApply: (offerIds: string[]) => void;
  /** "Descartar recomendación": clear the marks, keep the current selection. */
  onDismiss: () => void;
};

/**
 * "Asistente de ofertas": chat about the received offers on the award screen, with suggested
 * prompts. It never awards — a recommendation only pre-selects offers; the buyer confirms with the
 * award screen's own action.
 */
const OfferAssistantPanel = ({
  orderId,
  multipleOrderId,
  activeRecommendation,
  onRecommendation,
  onShowRecommendation,
  onApply,
  onDismiss,
}: OfferAssistantPanelProps) => {
  const { t } = useTranslation();
  const assistant = useOfferAssistant({ orderId, multipleOrderId });
  const { messages, isLoaded } = assistant;

  // Latest callbacks without re-running the detection effect when the parent re-renders.
  const callbacks = useRef({ onRecommendation, onShowRecommendation });
  callbacks.current = { onRecommendation, onShowRecommendation };

  // Ids of answers already accounted for. `null` until the stored conversation first loads: that
  // history is shown (marks only) but never pre-selected; only answers finishing afterwards are.
  const handled = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!isLoaded) return;
    const latest = latestRecommendationMessage(messages);
    if (handled.current === null) {
      handled.current = new Set(
        messages.filter((m) => m.status !== "PENDING").map((m) => m.id)
      );
      if (latest?.recommendation) {
        callbacks.current.onShowRecommendation(latest.recommendation);
      }
      return;
    }
    if (latest?.recommendation && !handled.current.has(latest.id)) {
      handled.current.add(latest.id);
      callbacks.current.onRecommendation(latest.recommendation);
    }
  }, [isLoaded, messages]);

  const send = (text: string, onSent?: () => void) => {
    assistant.clearSendError();
    assistant.send(text, onSent);
  };

  return (
    <Tile className="offer-assistant" aria-labelledby="offer-assistant-title">
      <div className="offer-assistant__header">
        <h3 id="offer-assistant-title" className="offer-assistant__title">
          {t("offerAssistant.title")}
        </h3>
        {/* ToggletipContent (not AILabelContent): Carbon's Popover can only attach its positioning
            ref to the former — AILabelContent is not forwardRef in this version. */}
        <AILabel
          size="xs"
          align="bottom-start"
          textLabel={t("offerAssistant.aiLabel")}
        >
          <ToggletipContent className="cds--ai-label-content">
            <p className="offer-assistant__ai-explanation">
              {t("offerAssistant.aiExplanation")}
            </p>
          </ToggletipContent>
        </AILabel>
      </div>
      <p className="offer-assistant__subtitle">
        {t("offerAssistant.subtitle")}
      </p>

      <div
        className="offer-assistant__chips"
        role="group"
        aria-label={t("offerAssistant.suggestionsLabel")}
      >
        {SUGGESTED_PROMPTS.map((key) => {
          const prompt = t(`offerAssistant.suggestions.${key}`);
          return (
            <OperationalTag
              key={key}
              type="purple"
              size="md"
              text={prompt}
              disabled={assistant.isBusy || !isLoaded}
              onClick={() => send(prompt)}
            />
          );
        })}
      </div>

      {assistant.isLoading && (
        <InlineLoading description={t("offerAssistant.loading")} />
      )}
      {assistant.isLoadError && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={t("offerAssistant.loadError")}
        />
      )}
      {isLoaded && (
        <OfferAssistantMessages
          messages={messages}
          onShowRecommendation={onShowRecommendation}
        />
      )}

      {assistant.sendErrorKey && (
        <InlineNotification
          kind={
            assistant.sendErrorKey === "offerAssistant.errors.pending"
              ? "warning"
              : "error"
          }
          lowContrast
          title={t(assistant.sendErrorKey)}
          onClose={() => {
            assistant.clearSendError();
          }}
        />
      )}

      {activeRecommendation && (
        <OfferAssistantRecommendationCard
          recommendation={activeRecommendation}
          onApply={onApply}
          onDismiss={onDismiss}
        />
      )}

      <OfferAssistantComposer
        disabled={assistant.isBusy || !isLoaded}
        onSend={send}
      />
    </Tile>
  );
};

export default OfferAssistantPanel;
